
import React from 'react';
import { Customer } from '@/lib/supabase/customers/types';
import { Edit, Trash2, Phone } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from '@/components/ui/button';

interface CustomersTableProps {
  customers: Customer[];
  selectedCustomers: string[];
  loading?: boolean;
  toggleCustomerSelection: (id: string) => void;
  handleEditCustomer: (id: string) => void;
  handleDeleteCustomer: (id: string) => void;
  formatDate: (dateString: string) => string;
  formatPhone: (phone?: string) => string;
  toggleSelectAll: () => void;
  allSelected: boolean;
  isDeleting?: boolean;
  onRefresh: () => void;
}

const CustomersTable = ({
  customers,
  selectedCustomers,
  loading,
  toggleCustomerSelection,
  handleEditCustomer,
  handleDeleteCustomer,
  formatDate,
  formatPhone,
  toggleSelectAll,
  allSelected,
  isDeleting,
  onRefresh
}: CustomersTableProps) => {
  return (
    <div className="reference-table-container">
      <Table>
        <TableHeader className="reference-table-header">
          <TableRow>
            <TableHead className="w-[50px] reference-table-cell-header">
              <Checkbox 
                checked={allSelected}
                onCheckedChange={toggleSelectAll}
                aria-label="Sélectionner tous les clients"
              />
            </TableHead>
            <TableHead className="reference-table-cell-header">Client</TableHead>
            <TableHead className="reference-table-cell-header">Téléphone</TableHead>
            <TableHead className="reference-table-cell-header">Créé le</TableHead>
            <TableHead className="reference-table-cell-header">Commandes</TableHead>
            <TableHead className="reference-table-cell-header">Commandes payées</TableHead>
            <TableHead className="reference-table-cell-header">Total dépensé</TableHead>
            <TableHead className="reference-table-cell-header">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={8} className="h-24 text-center">
                <div className="flex justify-center">
                  <div className="w-6 h-6 border-2 border-t-custom-primary rounded-full animate-spin"></div>
                </div>
              </TableCell>
            </TableRow>
          ) : customers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="h-24 text-center text-gray-500 dark:text-gray-400">
                Aucun client trouvé
              </TableCell>
            </TableRow>
          ) : (
            customers.map((customer) => (
              <TableRow 
                key={customer.id} 
                className={selectedCustomers.includes(customer.id) 
                  ? 'reference-table-row-selected' 
                  : 'reference-table-row'
                }
              >
                <TableCell>
                  <Checkbox 
                    checked={selectedCustomers.includes(customer.id)}
                    onCheckedChange={() => toggleCustomerSelection(customer.id)}
                    aria-label={`Sélectionner ${customer.name}`}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-indigo-100 dark:bg-indigo-900 mr-4 flex items-center justify-center">
                      <span className="text-indigo-600 dark:text-indigo-300 font-medium">
                        {customer.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-200">{customer.name}</div>
                      {customer.email && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">{customer.email}</div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="reference-table-cell">
                  {customer.phone ? (
                    <div className="flex items-center">
                      <Phone className="h-4 w-4 mr-2 text-gray-400 dark:text-gray-500" />
                      {formatPhone(customer.phone)}
                    </div>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">Non renseigné</span>
                  )}
                </TableCell>
                <TableCell className="reference-table-cell">
                  {customer.created_at ? formatDate(customer.created_at) : 'N/A'}
                </TableCell>
                <TableCell className="reference-table-cell">
                  {customer.orders_count || 0}
                </TableCell>
                <TableCell className="reference-table-cell">
                  {customer.paid_orders_count || 0}
                </TableCell>
                <TableCell className="reference-table-cell">
                  {customer.total_spent ? `${(customer.total_spent).toLocaleString('fr-FR')} DZD` : '0 DZD'}
                </TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEditCustomer(customer.id)}
                      className="reference-table-action-button"
                    >
                      <Edit className="h-4 w-4" />
                      <span className="sr-only">Modifier</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteCustomer(customer.id)}
                      className="reference-table-delete-button"
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">Supprimer</span>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default CustomersTable;
